import { useEffect, useRef } from 'react';

const DENOMS = [
  { value: '5',   base: '#8a9bb0' },
  { value: '10',  base: '#c07856' },
  { value: '20',  base: '#5b84b8' },
  { value: '50',  base: '#c69148' },
  { value: '100', base: '#64a580' },
  { value: '200', base: '#b8975a' },
  { value: '500', base: '#9c7496' },
];

function shade(hex, amt) {
  const m = hex.match(/^#?([a-f0-9]{6})$/i);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 255) * amt)));
  const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 255) * amt)));
  const b = Math.max(0, Math.min(255, Math.round((n & 255) * amt)));
  return `rgb(${r},${g},${b})`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function prerenderBill(denom, w, h, dpr) {
  const c = document.createElement('canvas');
  c.width = w * dpr;
  c.height = h * dpr;
  const ctx = c.getContext('2d');
  ctx.scale(dpr, dpr);

  const grad = ctx.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, shade(denom.base, 1.1));
  grad.addColorStop(0.55, denom.base);
  grad.addColorStop(1, shade(denom.base, 0.72));
  ctx.fillStyle = grad;
  roundRect(ctx, 0, 0, w, h, 4);
  ctx.fill();

  ctx.strokeStyle = 'rgba(255,255,255,0.14)';
  ctx.lineWidth = 0.8;
  roundRect(ctx, 3, 3, w - 6, h - 6, 2.5);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(0,0,0,0.2)';
  ctx.lineWidth = 0.8;
  roundRect(ctx, 0.5, 0.5, w - 1, h - 1, 4);
  ctx.stroke();

  ctx.fillStyle = 'rgba(255,255,255,0.82)';
  ctx.font = `700 ${Math.round(h * 0.34)}px 'Fraunces', 'Playfair Display', Georgia, serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(denom.value, 7, 6);
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText(denom.value, w - 7, h - 6);

  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.font = `500 ${Math.round(h * 0.6)}px 'Fraunces', Georgia, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('€', w / 2, h / 2 + 1);

  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 0.6;
  const stripeY = h * 0.72;
  ctx.beginPath();
  for (let i = 0; i < 14; i++) {
    const x = 10 + i * ((w - 20) / 14);
    ctx.moveTo(x, stripeY);
    ctx.lineTo(x, stripeY + h * 0.12);
  }
  ctx.stroke();

  const sheen = ctx.createLinearGradient(0, 0, w, h);
  sheen.addColorStop(0, 'rgba(255,255,255,0)');
  sheen.addColorStop(0.45, 'rgba(255,255,255,0.07)');
  sheen.addColorStop(0.55, 'rgba(255,255,255,0.12)');
  sheen.addColorStop(0.65, 'rgba(255,255,255,0.04)');
  sheen.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = sheen;
  roundRect(ctx, 0, 0, w, h, 4);
  ctx.fill();

  return c;
}

export default function FallingMoney() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    let bills = [];
    let sprites = new Map();

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const BILL_W = 110;
    const BILL_H = 55;

    for (const d of DENOMS) {
      sprites.set(d.value, prerenderBill(d, BILL_W, BILL_H, dpr));
    }

    const spawnBill = (initial) => {
      const denom = DENOMS[(Math.random() * DENOMS.length) | 0];
      const depth = 0.35 + Math.random() * 0.75;
      const scale = 0.55 + depth * 0.7;
      return {
        denom,
        depth,
        scale,
        x: Math.random() * width,
        y: initial
          ? Math.random() * height
          : -BILL_H * scale - Math.random() * 240,
        vx: (Math.random() - 0.5) * 0.4,
        vy: 0.25 + Math.random() * 0.7 + depth * 0.4,
        angle: (Math.random() - 0.5) * 0.9,
        angleVel: (Math.random() - 0.5) * 0.012,
        tumble: Math.random() * Math.PI * 2,
        tumbleSpeed: 0.012 + Math.random() * 0.028,
        opacity: 0.35 + depth * 0.5,
      };
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const density = Math.round((width * height) / 46000);
      const target = Math.max(14, Math.min(32, density));
      if (bills.length < target) {
        while (bills.length < target) bills.push(spawnBill(true));
      } else if (bills.length > target) {
        bills.length = target;
      }
    };

    resize();
    window.addEventListener('resize', resize);

    if (prefersReduced) {
      bills.forEach((b) => {
        b.vx = 0;
        b.vy = 0;
        b.tumbleSpeed = 0;
        b.angleVel = 0;
      });
      ctx.clearRect(0, 0, width, height);
      bills.sort((a, b) => a.depth - b.depth);
      drawAll();
      return () => window.removeEventListener('resize', resize);
    }

    function drawAll() {
      for (const b of bills) {
        const sprite = sprites.get(b.denom.value);
        if (!sprite) continue;
        const scaleX = Math.cos(b.tumble);
        const absX = Math.abs(scaleX);
        if (absX < 0.04) continue;

        ctx.save();
        ctx.globalAlpha = b.opacity * (0.6 + 0.4 * absX);
        ctx.translate(b.x, b.y);
        ctx.rotate(b.angle);
        ctx.scale(b.scale * scaleX, b.scale);
        ctx.drawImage(sprite, -BILL_W / 2, -BILL_H / 2, BILL_W, BILL_H);
        ctx.restore();
      }
    }

    let last = performance.now();
    let windPhase = Math.random() * 1000;
    let wind = 0;
    let gustTimer = 0;
    let gustBurst = 0;

    const tick = (now) => {
      const dt = Math.min(40, now - last);
      last = now;
      windPhase += dt * 0.0006;

      gustTimer -= dt;
      if (gustTimer <= 0) {
        gustTimer = 2600 + Math.random() * 4200;
        gustBurst = (Math.random() > 0.5 ? 1 : -1) * (1.4 + Math.random() * 2.2);
      }
      gustBurst *= 0.985;

      const baseWind =
        Math.sin(windPhase * 0.9) * 0.55 +
        Math.sin(windPhase * 0.31 + 1.2) * 0.35 +
        gustBurst;
      wind += (baseWind - wind) * 0.025;

      for (const b of bills) {
        const windForce = wind * (0.35 + b.depth * 0.8);
        b.vx += windForce * 0.022;
        b.vx *= 0.985;
        b.vy += 0.014 * (0.7 + b.depth * 0.6);
        const vMax = 1.3 + b.depth * 1.6;
        if (b.vy > vMax) b.vy = vMax;

        b.angleVel += windForce * 0.0009;
        b.angleVel *= 0.978;
        b.angle += b.angleVel;

        b.tumble += b.tumbleSpeed + windForce * 0.0015;

        b.x += b.vx;
        b.y += b.vy;

        const halfW = (BILL_W * b.scale) / 2 + 40;
        if (b.x < -halfW) b.x = width + halfW;
        if (b.x > width + halfW) b.x = -halfW;

        if (b.y > height + BILL_H * b.scale + 60) {
          Object.assign(b, spawnBill(false));
          b.x = Math.random() * width;
        }
      }

      bills.sort((a, b) => a.depth - b.depth);
      ctx.clearRect(0, 0, width, height);
      drawAll();

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
